import { defineStore } from "pinia";
import { useUserStore } from "./user";
import { useOrganisationStore } from "./organisation";
import SecureLS from "secure-ls";
import axios from "axios";
import { toRaw } from "vue";
import { jwtDecode } from "jwt-decode";
import router from "@/router";
var ls = new SecureLS({
  encodingType: "aes",
  isCompression: true,
  encryptionSecret:
    "Qu1x3NtdfpFDd5v7aoiAqtLHvRHNek8RAIZQOVMreHooJ2cf8Q1pG3PoKfrZfKa6",
});

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: {
      is_organisation_admin: false,
      is_super_admin: false,
    },
    user_profile: {},
    jwtPayload: null,
    tokenExpiry: null,
    TwoFactorRequired: false,
    isTwoFactorAuthVerify: false,
    isTwoFactorAuthSetupRequired: false,
    isOrganisationAdmin: false,
    isSuperAdmin: false,
    hasProduct: false,
    isVertexReporting: false,
    isSamlEnabled: false,
    orgStatus: "",
    setupCompleted: false,
    isLoggingOut: false,

    // Organisation State
    organisations: null,
    orgIndexCount: 0,

    organisation_users: [],
    organisation_settings: null,
    orgID: null,
    organisationName: null,
    isAutoPayment: false,

    // Product State
    products: null,
    organisation_products: {}, // Consider if this belongs here or in a separate product store
    vertex_roles: null, // Consider if this belongs in a separate roles/permissions store
    org_product_setup: [],
    product_toolbar: [],
    isOrgMandatoryTwoFaStatus: false,
    toolbar_cache: null,
  }),

  getters: {
    isTokenExpired: (state) => {
      if (!state.tokenExpiry) return true;
      return new Date() > new Date(state.tokenExpiry);
    },

    getTokenExpiry(state) {
      return state.tokenExpiry;
    },

    isOrganisationAdminUser(state) {
      return state.isOrganisationAdmin;
    },

    isSuperAdminUser(state) {
      return state.isSuperAdmin;
    },

    isUserAuthenticated(state) {
      return !!state.user?.id && !!state.jwtPayload;
    },
  },

  actions: {
    // --- Authentication Actions ---

    setLoggingOut(isLoggingOut) {
      this.isLoggingOut = isLoggingOut;
    },

    checkAuthentication() {
      if (!this.user) {
        this.logout();
      }
    },

    async logout(redirectRoute) {
      this.setLoggingOut(true);
      const userStore = useUserStore();
      const organisationStore = useOrganisationStore();

      const hasToken = !!this.tokenExpiry || !!this.jwtPayload;
      const wasExpired = hasToken && this.isTokenExpired;

      // Clear all state and local storage/cookies
      this.resetState();
      userStore.$reset();
      organisationStore.$reset();
      localStorage.removeItem("userId");
      document.cookie =
        "authToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;Secure";
      document.cookie =
        "authToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";

      if (redirectRoute !== false) {
        if (redirectRoute) {
          router.push(redirectRoute);
        } else if (wasExpired) {
          router.push("/login?logout=true&expired=true");
        } else {
          router.push("/login");
        }
      }
    },

    authenticate(authenticated) {
      this.isAuthenticated = authenticated;
    },

    setJwtPayloadData() {
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
        return null;
      };

      if (!this.jwtPayload) {
        const token = getCookie("authToken");
        try {
          if (token && typeof token === "string") {
            this.jwtPayload = jwtDecode(token);
          } else if (this.user && typeof this.user.access_token === "string") {
            this.jwtPayload = jwtDecode(this.user.access_token);
          }
        } catch (e) {
          console.error("Error decoding token in setJwtPayloadData:", e);
        }
      }

      if (!this.jwtPayload) {
        return;
      }

      if (!this.user) {
        this.user = {};
      }

      const cookieToken = getCookie("authToken");
      if (cookieToken && !this.user.access_token) {
        this.user.access_token = cookieToken;
      }

      if (this.jwtPayload.user_id) {
        this.user.id = this.jwtPayload.user_id;
      }

      if (this.jwtPayload.first_name) {
        this.user.first_name = this.jwtPayload.first_name;
      }

      if (this.jwtPayload.last_name) {
        this.user.last_name = this.jwtPayload.last_name;
      }

      if (this.jwtPayload.exp) {
        this.setTokenExpiry(new Date(this.jwtPayload.exp * 1000));
      } else if (this.jwtPayload.expires_at) {
        this.setTokenExpiry(new Date(this.jwtPayload.expires_at));
      }

      // VertexRoles
      if (this.jwtPayload.roles && Array.isArray(this.jwtPayload.roles)) {
        this.isSuperAdmin = this.jwtPayload.roles.includes("super_admin");
        if (this.user) {
          this.user.is_super_admin = this.isSuperAdmin;
        }
        this.isVertexReporting =
          this.jwtPayload.roles.includes("vertex_reporting");
      }

      if (
        this.jwtPayload.organisation_role &&
        Array.isArray(this.jwtPayload.organisation_role) &&
        this.jwtPayload.organisation_role.length > 0
      ) {
        this.user.is_organisation_admin = !!this.jwtPayload.organisation_role[0].is_organisation_admin;
        this.isOrganisationAdmin = !!this.jwtPayload.organisation_role[0].is_organisation_admin;
        
        this.orgStatus = this.jwtPayload.organisation_role[0].org_status;
        this.orgID = this.jwtPayload.organisation_role[0].organisation_id;
        this.organisationName = this.jwtPayload.organisation_role[0].organisation;

        // Check if user has access to products
        if (this.jwtPayload.organisation_role[0].products) {
          this.hasProduct = true;
        }

        // Also ensure organisationStore is in sync
        const organisationStore = useOrganisationStore();
        if (!organisationStore.organisation?.id || organisationStore.organisation.id !== this.orgID) {
          organisationStore.organisation = {
            id: this.orgID,
            org_status: this.orgStatus,
          };
        }
      } else {
        if (this.user) {
          this.user.is_organisation_admin = false;
        }
        this.isOrganisationAdmin = false;
        this.orgStatus = "";
        this.orgID = null;
        this.organisationName = null;
        this.hasProduct = false;
      }

    },

    resetState() {
      // Pinia's built-in reset functionality is very useful here
      this.$reset();
    },

    setAccessToken(accessToken) {
      this.accessToken = accessToken;
    },

    setJwtPayload(payload) {
      this.jwtPayload = payload;
      this.setJwtPayloadData();
    },

    setTokenExpiry(expiryTime) {
      this.tokenExpiry = expiryTime;
    },

    setOrganisationAdminStatus(isAdmin) {
      this.isOrganisationAdmin = isAdmin;
    },

    setSuperAdminStatus(isSuperAdmin) {
      this.isSuperAdmin = isSuperAdmin;
    },

    setOrgStatus(orgStatus) {
      this.orgStatus = orgStatus;
    },

    // --- User Actions ---
    async getUserByID(id) {
      try {
        const response = await axios.get(`/users/${id}`);
        this.user_profile = response.data.user;
      } catch (err) {
        console.error("Error getting user profile:", err);
        // Optionally, show a toast message to the user
        // this.$toast.error("Failed to load user profile.");
      }
    },

    setUser(user) {
      this.user = { ...toRaw(user) };
      this.preAuthToken = null; // Clear pre-auth token on full successful login

      // Add persistence logic
      if (user && user.id) {
        localStorage.setItem("userId", user.id);
        const d = new Date();
        d.setTime(d.getTime() + 4 * 60 * 60 * 1000); // Expires in 4 hours
        const expires = "expires=" + d.toUTCString();
        document.cookie = `authToken=${user.access_token};${expires};path=/;SameSite=Lax;Secure`;

        // Update organisation store with the new user's organisation to avoid stale persisted data
        const organisationStore = useOrganisationStore();
        if (user.organisation_access) {
          organisationStore.organisation = {
            id: user.organisation_access.id,
            org_status: user.organisation_access.org_status,
          };
          this.orgID = user.organisation_access.id;
          this.orgStatus = user.organisation_access.org_status;
        } else if (user.organisation) {
          organisationStore.organisation = {
            id: user.organisation.id,
            org_status: user.organisation.org_status || user.organisation.status,
          };
          this.orgID = user.organisation.id;
          this.orgStatus = user.organisation.org_status || user.organisation.status;
        }
      } else {
        localStorage.removeItem("userId");
        document.cookie =
          "authToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      }
    },

    setUserId(id) {
      this.user = {}; // Initialize to ensure it's an object before setting id
      this.user.id = id;

      // Add persistence logic
      if (id) {
        localStorage.setItem("userId", id);
      } else {
        localStorage.removeItem("userId");
      }
    },

    // --- Two-Factor Authentication (2FA) Actions ---
    setTwoFactorAuthSetupRequired(isRequired) {
      this.isTwoFactorAuthSetupRequired = isRequired;
    },

    setTwoFactorAuthVerify(isVerified) {
      this.isTwoFactorAuthVerify = isVerified;
    },

    setTwoFactorRequired(required) {
      this.TwoFactorRequired = required;
    },

    // JWT
    checkOrganisationAdminRole() {
      if (!this.user?.access_token || typeof this.user.access_token !== "string") {
        return;
      }
      try {
        let jwtPayload = jwtDecode(this.user.access_token);
        // Check if user is an organisation admin
        if (jwtPayload.organisation_role && jwtPayload.organisation_role[0] && jwtPayload.organisation_role[0].is_organisation_admin) {
          this.user.is_organisation_admin = true;
        }
      } catch (e) {
        console.error("Error decoding token in checkOrganisationAdminRole:", e);
      }
    },

    // --- Organisation Actions ---
    async fetchOrganisationSamlStatus(organisationID) {
      try {
        const response = await axios.get(`/users/organisations/${organisationID}/saml-status`);
        if (response.data.success) {
          this.isSamlEnabled = response.data.is_saml_sso;
        } else {
          console.error("Could not fetch SAML status:", response.data.message);
        }
      } catch (err) {
        console.error("An error occurred while fetching SAML status:", err);
      }
    },

    async activateOrganisation() {
      const organisationStore = useOrganisationStore();
      try {
        const resp = await axios.put(
          `/orgadmin/organisations/${organisationStore.organisation.id}/activate`
        );

        if (!resp.data.success) {
          this.$toast.error(resp.data.message);
          return;
        }
        await organisationStore.getOrganisation(organisationStore.organisation.id);
        this.orgStatus = organisationStore.organisation.org_status;
        this.setupCompleted = true;
      } catch (e) {
        console.error(e);
      }
    },

    async getOrgProductSetup() {
      if (!this.isOrganisationAdmin) {
        return;
      }
      try {
        const resp = await axios.get(
          `/orgadmin/organisations/${this.orgID}/product-setup`
        );

        if (resp.data.success) {
          const products = resp.data.organisation_products_setup;
          if (Array.isArray(products)) {
            const seenApplications = new Set();
            this.org_product_setup = products.filter((item) => {
              if (!item.application || seenApplications.has(item.application)) {
                return false;
              } else {
                seenApplications.add(item.application);
                return true;
              }
            });
          }
        } else {
          this.$toast.error(resp.data.message);
        }
      } catch (err) {
        console.error(err);
      }
    },


    async getOrganisationProductToolbar(orgId = null, forceRefresh = false) {
      if (this.isLoggingOut) {
        return;
      }
      const activeOrgId = orgId || this.orgID || useOrganisationStore().organisation?.id;
      if (!activeOrgId) {
        console.warn("getOrganisationProductToolbar: No valid Organisation ID available.");
        return;
      }

      if (!forceRefresh && this.toolbar_cache && this.toolbar_cache.orgId === activeOrgId && !this.toolbar_cache.userId) {
        return;
      }

      try {
        const resp = await axios.get(
          `/orgadmin/organisations/${activeOrgId}/product-toolbar`
        );

        if (resp.data.success) {
          const products = resp.data.toolbar_products;
          if (Array.isArray(products)) {
            const seenApplications = new Set();
            this.product_toolbar = products.filter((item) => {
              if (!item.application || seenApplications.has(item.application)) {
                return false;
              } else {
                seenApplications.add(item.application);
                return true;
              }
            });
            this.toolbar_cache = {
              orgId: activeOrgId,
              userId: null,
            };
          }
        } else {
          this.$toast.error(resp.data.message);
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getUserProductToolbar(forceRefresh = false) {
      if (
        this.isLoggingOut ||
        !this.user?.id ||
        this.user.is_organisation_admin ||
        this.user.is_super_admin
      ) {
        return;
      }

      const activeUserId = this.user.id;
      const activeOrgId = this.orgID || useOrganisationStore().organisation?.id;

      if (!forceRefresh && this.toolbar_cache && this.toolbar_cache.userId === activeUserId && this.toolbar_cache.orgId === activeOrgId) {
        return;
      }

      try {
        const resp = await axios.get(`/users/${activeUserId}/product-toolbar`);

        if (resp.data.success) {
          this.product_toolbar = resp.data.toolbar_products;
          this.toolbar_cache = {
            orgId: activeOrgId,
            userId: activeUserId,
          };
        } else {
          this.$toast.error(resp.data.message);
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getOrganisations() {
      try {
        const response = await axios.get(`/supadmin/organisations`);
        if (!response.data.success) {
          console.error("Error fetching all organisations:", response.data.error);
          return;
        }
        this.organisations = response.data.organisations;
      } catch (err) {
        console.error("Error fetching all organisations:", err);

      }
    },

    async getOrganisationUsers(id) {
      try {
        const response = await axios.get(`orgadmin/users/organisations/${id}`);
        this.organisation_users = response.data.users;
      } catch (err) {
        console.error("Error getting organisation users:", err);
        this.$toast.error("Failed to load organisation users.");
      }
    },

    async getSelectedOrganisation(id) {

      const organisationStore = useOrganisationStore();
      // This function essentially duplicates `getOrganisation`.
      // It's better to call the existing `getOrganisation` directly.
      await organisationStore.getOrganisation(id);

      if (!this.organisation || this.organisation.id !== id) {
        console.error(`Organisation with ID ${id} not found after fetch.`);
        // Optionally, show a toast or throw an error for upstream handling
        // this.$toast.error("Selected organisation could not be found.");
      }
    },

    setOrgID(orgid) {
      this.orgID = orgid;
    },

    setOrgIndexCount(index) {
      this.orgIndexCount = index;
    },

    setOrganisations(organisations) {
      try {
        // Assume 'organisations' is already resolved here, or handle the promise
        this.organisations = organisations;
      } catch (error) {
        console.error("Error setting organisation in store:", error);
        this.$toast.error("Error setting organisation: store");
      }
    },

    setOrganisationSettings(settings) {
      this.organisation_settings = settings;
    },

    setOrganisationUsers(organisation_users) {
      this.organisation_users = organisation_users;
    },

    // --- Product & Role Actions ---
    async getProductsFromStore() {
      if (!this.user.is_super_admin) {
        // This seems to imply a product store, but if products are specific to super admin view
        // it might make sense here. If other users also need products, consider a separate store.
        return;
      }

      try {
        const response = await axios.get(`/supadmin/products`);
        this.products = response.data.products; // Save the data in the store's state
      } catch (error) {
        console.error("Failed to fetch products:", error);
        this.$toast.error("Failed to load products.");
      }
    },

    setProducts(products) {
      this.products = products;
    },

    setSelectedOrgProducts(org_products) {
      this.organisation.organisation_products = org_products;
    },

    setVertexRoles(roles) {
      this.vertex_roles = roles;
    },

    hasReportingActionAccess() {
      let hasAccess = false;

      if (this.isSuperAdmin) hasAccess = true;

      if (this.isVertexReporting) hasAccess = false;

      return hasAccess;
    },

    async login(credentials) {
      try {
        const response = await axios.post(`/users/login`, credentials);
        if (response.data.success) {
          this.setUserId(response.data.id);

          if (response.data.fido_required || response.data.two_factor_required) {
            this.preAuthToken = response.data.pre_auth_token;
            this.user.access_token = null; // Do not store in user.access_token to avoid premature API authorization
            return {
              success: true,
              fido_required: response.data.fido_required,
              fido_register_required: response.data.fido_register_required,
              fido_optional: response.data.fido_optional,
              two_factor_required: response.data.two_factor_required,
              two_factor_setup: response.data.two_factor_setup,
              pre_auth_token: response.data.pre_auth_token,
            };
          }

          await this.setUser(response.data.user);

          // Logic from handleSuccessfulLogin
          const { user } = response.data;
          const accessToken = user.access_token;

          if (!accessToken) {
            return { success: false, message: "Empty access token" };
          }

          const jwtPayload = jwtDecode(accessToken);
          this.setJwtPayload(jwtPayload);

          // We don't handle redirection here, we return the user and permissions info
          // so the component can decide where to go.

          return {
            success: true,
            two_factor_required: false,
            user: response.data.user,
            jwtPayload: jwtPayload
          };
        } else {
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        console.error(error);
        return { success: false, message: error.message || "An error occurred during login." };
      }
    },

    async beginFidoRegistration() {
      try {
        const response = await axios.get('/webauthn/register/begin');
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.message || "Failed to start FIDO registration." };
      }
    },

    async finishFidoRegistration(payload) {
      try {
        const response = await axios.post('/webauthn/register/finish', payload);
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.message || "Failed to complete FIDO registration." };
      }
    },

    async beginFidoLogin(tempToken) {
      try {
        const response = await axios.post('/webauthn/login/begin', { temp_token: tempToken });
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.message || "Failed to start FIDO verification." };
      }
    },

    async finishFidoLogin(payload) {
      try {
        const response = await axios.post('/webauthn/login/finish', payload);
        if (response.data.success) {
          await this.setUser(response.data.user);
          const { user } = response.data;
          const accessToken = user?.access_token;
          if (accessToken) {
            const jwtPayload = jwtDecode(accessToken);
            this.setJwtPayload(jwtPayload);
          }
        }
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.message || "Failed to complete FIDO verification." };
      }
    },

    async adminResetFido(targetUserId) {
      try {
        const response = await axios.post('/webauthn/admin/reset', { target_user_id: targetUserId });
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.response?.data?.message || error.message || "Failed to reset security key." };
      }
    },

    async trustFidoDevice(tempToken) {
      try {
        const token = tempToken || this.user?.access_token;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.post('/webauthn/trust-device', { temp_token: token }, { headers });
        return response.data;
      } catch (error) {
        console.error(error);
        return { success: false, message: error.response?.data?.message || error.message || "Failed to trust device." };
      }
    },
  },

  persist: {
    key: "vertex_auth",
    storage: {
      getItem: (key) => ls.get(key),
      setItem: (key, value) => ls.set(key, value),
      removeItem: (key) => ls.remove(key),
    },
  },
});
